import React from 'react';
import { Crown, Building2, Sparkles } from 'lucide-react';

/**
 * ModeToggle - Toggle button for switching between BLACKI and Non-BLACKI insights
 * Shows in report header
 */
const ModeToggle = ({ currentMode, onToggle, isBothReady, isGenerating }) => {
  const modes = {
    BLACKI: {
      icon: Crown,
      label: 'BLACKI',
      subtitle: 'Premier',
      color: 'amber',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-300',
      activeBg: 'bg-amber-500',
      activeText: 'text-white'
    },
    NON_BLACKI: {
      icon: Building2,
      label: 'Non-BLACKI',
      subtitle: 'Other IIMs',
      color: 'blue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-300',
      activeBg: 'bg-blue-500',
      activeText: 'text-white'
    }
  };

  const currentConfig = modes[currentMode];
  const otherMode = currentMode === 'BLACKI' ? 'NON_BLACKI' : 'BLACKI';
  const otherConfig = modes[otherMode];

  return (
    <div className="flex items-center gap-3">
      {/* Label */}
      <span className="text-sm text-gray-500 hidden sm:inline">
        View Mode:
      </span>

      {/* Toggle Container */}
      <div className="relative flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
        
        {/* Current Mode Badge */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${currentConfig.bgColor} ${currentConfig.textColor} border ${currentConfig.borderColor}`}>
          <currentConfig.icon className="w-4 h-4" />
          <span className="text-sm font-semibold">{currentConfig.label}</span>
        </div>

        {/* Switch Button */}
        <button
          onClick={onToggle}
          disabled={!isBothReady || isGenerating}
          className={`ml-1 flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
            isBothReady && !isGenerating
              ? 'hover:bg-white hover:shadow-sm text-gray-600'
              : 'text-gray-400 cursor-not-allowed'
          }`}
          title={
            !isBothReady 
              ? 'Other mode is still generating...' 
              : isGenerating 
                ? 'Generating...' 
                : `Switch to ${otherConfig.label} view`
          }
        >
          {isGenerating ? (
            <>
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              <span className="hidden sm:inline">Generating...</span>
            </>
          ) : !isBothReady ? (
            <>
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              <span className="hidden sm:inline">Loading...</span>
            </>
          ) : (
            <>
              <otherConfig.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{otherConfig.label}</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </>
          )}
        </button>
      </div>

      {/* New Feature Badge (shows when both ready for first time) */}
      {isBothReady && (
        <div className="hidden md:flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
          <Sparkles className="w-3 h-3" />
          <span>Both views ready!</span>
        </div>
      )}
    </div>
  );
};

export default ModeToggle;
